import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Shield, Leaf, Smile, Sparkles, Watch } from "lucide-react";
import WelfareSupremacy from "@/components/site/WelfareSupremacy";

const GALLERY = [
  { src: "/photos/pawsitive-reading.jpg", caption: "Reading session · veteran service dog supporting a young reader." },
  { src: "/photos/valor-reading.jpg", caption: "Working partner offering calm during a reading program." },
  { src: "/photos/pagan-and-trump.jpg", caption: "A rural visit — quiet presence, steady focus." },
  { src: "/photos/acf-moose.jpg", caption: "An ACF Serve Team member ready to deploy." },
];

const PROTOCOLS = [
  {
    icon: Shield,
    t: "Selection",
    b: "Temperament, biology, and disposition are evaluated against the work itself — not against demand. A dog earns into the role; the role never compels the dog.",
  },
  {
    icon: Leaf,
    t: "Training",
    b: "Relationship-led, force-free methodology. We measure mastery by the dog's willingness, never by compliance. Cumulative-stress monitoring is built into every session.",
  },
  {
    icon: Watch,
    t: "Deployment",
    b: "Posture, proximity, and duration adapt to the environment's tier (I, II, or III). Handlers carry absolute halt-authority — no exceptions.",
  },
  {
    icon: Heart,
    t: "Recovery",
    b: "Mandatory recovery windows follow every deployment. Recovery is doctrine — not an option, never abbreviated for institutional convenience.",
  },
  {
    icon: Smile,
    t: "Joy Preservation",
    b: "Play, rest, and curiosity are protected with the same discipline as deployment readiness. A working dog without joy is a system in failure.",
  },
  {
    icon: Sparkles,
    t: "Lifetime Stewardship",
    b: "Our partners are cared for from selection through retirement — for the entirety of their lives. Retirement is dignified, planned, and protected.",
  },
];

export default function CaninePartnersPage() {
  useEffect(() => {
    document.title = "Canine Partners · MSWDC";
  }, []);

  return (
    <main data-testid="partners-page" className="bg-white">
      {/* Header */}
      <section className="border-b border-slate-200 bg-[#FAF7EC]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-24 md:pt-32 pb-20 md:pb-28">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 md:col-span-8">
              <div className="flex items-center gap-3">
                <span className="brand-rule" />
                <span className="overline">Our Canine Partners</span>
              </div>
              <h1 className="mt-6 font-display font-black text-4xl sm:text-5xl md:text-7xl tracking-tighter leading-[1.02] text-slate-900">
                Living partners.
                <br />
                <span className="text-amber-600">Not tools.</span>
              </h1>
              <p className="mt-8 max-w-2xl text-slate-700 text-base md:text-lg leading-relaxed">
                Every dog who serves under the MSWDC banner is a partner first and a
                practitioner second. Welfare is not a policy consideration — it is the
                ethical ceiling of the entire institution.
              </p>
            </div>
            <div className="col-span-12 md:col-span-4 md:text-right">
              <Link
                to="/donate"
                data-testid="partners-donate-cta"
                className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-700 text-white px-6 py-4 font-semibold text-sm transition-colors"
              >
                Support our partners <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 md:col-span-5">
              <div className="flex items-center gap-3">
                <span className="brand-rule" />
                <span className="overline">In the field</span>
              </div>
              <h2 className="mt-6 font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05] text-slate-900">
                Real moments. Real partners.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7 md:pt-12">
              <p className="text-slate-600 leading-relaxed">
                Scenes from the Coalition's everyday work — schools, reading programs,
                veteran-serving events, rural community spaces. Quiet, consistent, deliberate.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-14">
            {GALLERY.map((g, i) => (
              <figure key={i} data-testid={`partner-photo-${i}`} className="group">
                <div className="overflow-hidden border border-slate-200">
                  <img
                    src={g.src}
                    alt={g.caption}
                    loading="lazy"
                    className="w-full h-[420px] md:h-[520px] object-cover group-hover:scale-[1.02] transition-transform duration-700 ease-out"
                  />
                </div>
                <figcaption className="mt-4 flex items-start gap-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-600 pt-1 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-slate-600 leading-relaxed text-[15px]">{g.caption}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <WelfareSupremacy />

      {/* Operating Protocols */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-5">
              <div className="flex items-center gap-3">
                <span className="brand-rule" />
                <span className="overline">Operating Protocols</span>
              </div>
              <h2 className="mt-6 font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05] text-slate-900">
                From selection to retirement, governed by doctrine.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7 md:pt-12">
              <p className="text-slate-600 leading-relaxed">
                Each phase of a working dog's life with MSWDC is structured to protect
                their well-being — and, by extension, the integrity of every deployment
                we offer.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {PROTOCOLS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.t}
                  data-testid={`protocol-${i}`}
                  className="border-t-2 border-slate-200 hover:border-amber-600 pt-6 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <Icon size={20} strokeWidth={1.5} className="text-slate-400" />
                  </div>
                  <h3 className="mt-4 font-display font-bold text-xl md:text-2xl text-slate-900">
                    {p.t}
                  </h3>
                  <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">{p.b}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A1128] text-white grain">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-28">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 md:col-span-8">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-500">
                A dog's welfare is the work's foundation
              </div>
              <h2 className="mt-4 font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05]">
                Help us protect the partners that make this possible.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:text-right space-y-3">
              <Link
                to="/donate"
                data-testid="partners-cta-donate"
                className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-700 px-6 py-4 font-semibold text-sm w-full md:w-auto justify-center"
              >
                Fund welfare <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="partners-cta-contact"
                className="inline-flex items-center gap-3 border border-white/30 hover:border-amber-500 hover:text-amber-500 px-6 py-4 font-semibold text-sm w-full md:w-auto justify-center transition-colors"
              >
                Partner with us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
